import React from "react";
import { useEffect, useRef, useState } from "react";
import styles from "./styles.module.css";
import { FaAngleDown, FaAngleUp } from "react-icons/fa6";
export function Dropdown({
  options = [],
  value,
  onChange,
  defaultValue,
  align = "left",
  className,
  placeholder = "Select an option",
  trigger,
  children,
}) {
  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(defaultValue);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close dropdown when pressing escape
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleItemClick = (selectedValue, selectedLabel) => {
    if (onChange) {
      onChange(selectedValue);
    } else {
      setInternalValue(selectedValue);
    }
    setOpen(false);
  };

  const currentValue = value !== undefined ? value : internalValue;
  const selectedOption = options.find(
    (option) => option.value === currentValue
  );

  const renderTrigger = () => {
    if (trigger) {
      return React.cloneElement(trigger, {
        onClick: () => setOpen(!open),
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(!open);
          }
        },
        tabIndex: 0,
        role: "button",
        "aria-haspopup": "true",
        "aria-expanded": open,
        "data-selected-label": selectedOption
          ? selectedOption.label
          : placeholder,
      });
    }

    return (
      <div
        onClick={() => setOpen(!open)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(!open);
          }
        }}
        tabIndex={0}
        role="button"
        aria-haspopup="true"
        aria-expanded={open}
        className={styles.dropdownTrigger}
      >
        <input
          className={styles.input}
          value={selectedOption ? selectedOption.label : placeholder}
        />

        <div className={styles.arrow}>
          {open ? <FaAngleUp /> : <FaAngleDown />}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (children) {
      return React.Children.map(children, (child) => {
        if (React.isValidElement(child) && child.type === DropdownItem) {
          return React.cloneElement(child, {
            onClick: () =>
              handleItemClick(
                child.props.value || child.props.children,
                child.props.children
              ),
            "aria-selected":
              child.props.value === currentValue ||
              child.props.children === currentValue,
          });
        }
        return child;
      });
    }

    return options.map((option) => (
      <div
        key={option.value}
        onClick={() => handleItemClick(option.value, option.label)}
        className={`${styles.dropdownItem} ${
          option.value === currentValue ? styles.selected : ""
        }`}
        role="menuitem"
        tabIndex={-1}
        data-value={option.value}
        aria-selected={option.value === currentValue}
      >
        {option.label}
      </div>
    ));
  };

  return (
    <div className={styles.dropdownContainer} ref={dropdownRef}>
      {renderTrigger()}

      {open && (
        <div
          className={`${styles.dropdownContent} ${
            align === "right" ? styles.alignRight : styles.alignLeft
          } ${className || ""}`}
        >
          {renderContent()}
        </div>
      )}
    </div>
  );
}

export function DropdownItem({
  children,
  onClick,
  disabled = false,
  className,
  value,
}) {
  return (
    <div
      onClick={disabled ? undefined : onClick}
      className={`${styles.dropdownItem} ${disabled ? styles.disabled : ""} ${
        className || ""
      }`}
      role="menuitem"
      tabIndex={-1}
      data-disabled={disabled || undefined}
      data-value={value}
    >
      {children}
    </div>
  );
}

export function DropdownSeparator({ className }) {
  return <div className={`${styles.dropdownSeparator} ${className || ""}`} />;
}

export function DropdownLabel({ children, className }) {
  return (
    <div className={`${styles.dropdownLabel} ${className || ""}`}>
      {children}
    </div>
  );
}
